import React, { useState } from "react";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { ResourceMainSettingsItem } from "@web-src/modules/common/components";
import { useMe } from "@web-src/modules/auth/hooks/useMe";
import { useUserProfileProviderContext } from "@jugl-web/domain-resources/users/providers/UserProfileProvider";
import { useFileSelect } from "@jugl-web/domain-resources/files/hooks/useFileSelect";
import { avatarFileSelectConfig } from "@jugl-web/domain-resources/files/consts";
import { AVATAR_VERSION_STORAGE_KEY } from "@jugl-web/utils/storage";
import { AvatarSelectSize } from "@jugl-web/ui-kit";
import { useUserRoleName } from "@jugl-web/domain-resources/users/hooks/useUserRoleName";
import { MainNavBar } from "@web-src/modules/navigation/components/MainNavBar";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useDeleteAccountDialog } from "@web-src/modules/auth/components/DeleteAccountDialog/useDeleteAccountDialog";
import { ReactComponent as BinIcon } from "./assets/bin.svg";
import { ReactComponent as EditIcon } from "./assets/edit.svg";
import { UserAccountSettingsLayout } from "../../components/UserAccountSettingsLayout";
import { LoginMethodList } from "./components/LoginMethodList";
import { EditProfileNameDialog } from "./components/EditProfileNameDialog";

const MainSettings: React.FC = () => {
  const { entityId, subscriptionInfo } = useEntitySelectedProvider();
  const { t } = useTranslations();
  const { open } = useDeleteAccountDialog();
  const { me } = useMe();
  const { getRoleName } = useUserRoleName();
  const { isCx } = useAppVariant();

  const [isEditProfileNameDialogOpen, setIsEditProfileNameDialogOpen] =
    useState(false);

  const { updateUserProfilePicture } = useUserProfileProviderContext();

  const { selectFile } = useFileSelect();

  const getAvatarVersion = () =>
    localStorage.getItem(AVATAR_VERSION_STORAGE_KEY) || "1";
  const [avatarVersion, setAvatarVersion] = useState(getAvatarVersion());

  const handleImageChange = (image?: File) => {
    if (!me) {
      return;
    }

    updateUserProfilePicture({
      entityId,
      userId: me.id,
      file: image ?? null,
    })
      .then(() => {
        if (image) {
          const newAvatarVersion = Math.random().toString();
          localStorage.setItem(AVATAR_VERSION_STORAGE_KEY, newAvatarVersion);
          setAvatarVersion(newAvatarVersion);
        }
      })
      .catch(() => {
        // Do nothing
      });
  };

  if (!me) return null;

  const departmentName = me.spaces[0]?.info.title;

  const $content = (
    <div className="flex flex-col gap-10 px-8 py-12">
      <ResourceMainSettingsItem
        title={me.display_name}
        avatarSelectProps={{
          defaultSrc: me.profile?.img
            ? `${me.profile.img}?v=${avatarVersion}`
            : undefined,
          onSave: handleImageChange,
          size: AvatarSelectSize.xxl,
          showRemoveButton: true,
          externalSelectFile: () => selectFile(avatarFileSelectConfig),
        }}
        details={[
          {
            title: `${t({
              id: "settings-page.role",
              defaultMessage: "Role",
            })}:`,
            content: getRoleName(me.role),
          },
          ...(!isCx
            ? [
                {
                  title: `${t({
                    id: "settings-page.team",
                    defaultMessage: "Team",
                  })}:`,
                  content: departmentName || (
                    <span className="text-grey-700">
                      {t({
                        id: "common.none",
                        defaultMessage: "None",
                      })}
                    </span>
                  ),
                },
              ]
            : []),
        ]}
        menuItems={[
          {
            id: "edit-profile-name",
            label: t({
              id: "settings-page.edit-profile-name",
              defaultMessage: "Edit Profile name",
            }),
            icon: <EditIcon />,
            onSelect: (_, close) => {
              setIsEditProfileNameDialogOpen(true);
              close();
            },
            isHidden: !isCx,
          },
          {
            id: "delete-my-account",
            label: t({
              id: "settings-page.delete-my-account",
              defaultMessage: "Delete My Account",
            }),
            icon: <BinIcon />,
            onSelect: (_, close) => {
              open();
              close();
            },
          },
        ]}
      />
      <LoginMethodList />
    </div>
  );

  return (
    <>
      {!isCx && !subscriptionInfo?.planIsActive && <MainNavBar />}

      {isCx ? (
        $content
      ) : (
        <UserAccountSettingsLayout selectedTab="main-settings">
          {$content}
        </UserAccountSettingsLayout>
      )}

      <EditProfileNameDialog
        isOpen={isEditProfileNameDialogOpen}
        onRequestClose={() => setIsEditProfileNameDialogOpen(false)}
      />
    </>
  );
};

export default MainSettings;
